import { redirect } from "react-router";

interface ApiClientOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: object | FormData;
  headers?: Record<string, string>;
  skipAuthRedirect?: boolean;
}

interface ApiResponse<T = any> {
  data: T;
  status: number;
  detail?: string;
}

interface User {
  first_name?: string;
  is_onboarded?: boolean;
  is_creating_team?: boolean;
  team_id?: number;
}

export interface ApiError extends Error {
  status?: number;
  responseData?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function readDetail(value: unknown): string | undefined {
  if (typeof value === "string" && value) {
    return value;
  }
  const record = asRecord(value);
  if (record) {
    if (typeof record.message === "string" && record.message) {
      return record.message;
    }
    if (typeof record.detail === "string" && record.detail) {
      return record.detail;
    }
  }
  if (Array.isArray(value)) {
    const first = value[0];
    if (first && typeof first === "object" && "msg" in first) {
      const msg = (first as { msg?: unknown }).msg;
      if (typeof msg === "string" && msg) {
        return msg;
      }
    }
  }
  return undefined;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const apiError = error as ApiError;
  const data = asRecord(apiError.responseData);
  const fromDetail = readDetail(data?.detail);
  if (fromDetail) {
    return fromDetail;
  }
  const nested = asRecord(data?.data);
  const fromNested = readDetail(nested?.detail);
  if (fromNested) {
    return fromNested;
  }

  if (
    error instanceof Error &&
    error.message &&
    !error.message.startsWith("HTTP error!")
  ) {
    return error.message;
  }

  return fallback;
}

export function getApiErrorStatus(error: unknown): number | undefined {
  return (error as ApiError).status;
}

class ApiClient {
  private baseUrl: string;
  private user: User | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_ENDPOINT || "";
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  clearLocalState() {
    this.user = null;
  }

  async logout(): Promise<void> {
    try {
      await this.request("/auth/signout", { method: "POST" });
      this.clearLocalState();
    } catch (error) {
      console.warn("Logout API call failed:", error);
      throw error;
    }
  }

  setUser(user: User) {
    this.user = user;
  }

  getUser(): User | null {
    return this.user;
  }

  private getHeaders(
    customHeaders?: Record<string, string>,
    isFormData: boolean = false
  ): Record<string, string> {
    const headers: Record<string, string> = {
      ...customHeaders,
    };
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }
    if (this.user?.team_id) {
      headers["Team-ID"] = this.user.team_id.toString();
    }

    return headers;
  }

  async request<T = any>(
    route: string,
    options: ApiClientOptions = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = "GET",
      body,
      headers: customHeaders,
      skipAuthRedirect = false,
    } = options;
    const isFormData = body instanceof FormData;
    const requestOptions: RequestInit = {
      method,
      headers: this.getHeaders(customHeaders, isFormData),
      credentials: "include",
    };

    if (body && method !== "GET") {
      if (isFormData) {
        requestOptions.body = body;
      } else {
        requestOptions.body = JSON.stringify(body);
      }
    }

    try {
      const response = await fetch(`${this.baseUrl}${route}`, requestOptions);

      if (response.status === 401 && !skipAuthRedirect) {
        this.clearLocalState();
        throw redirect("/login");
      }

      if (response.status === 404 && !skipAuthRedirect) {
        throw redirect("/404");
      }

      const data = response.ok
        ? await response.json()
        : await response.json().catch(() => null);

      if (!response.ok) {
        const error = new Error(
          `HTTP error! status: ${response.status}`
        ) as ApiError;
        error.status = response.status;
        error.responseData = data;
        throw error;
      }

      return { data, status: response.status };
    } catch (error) {
      if (
        error instanceof Response ||
        (error instanceof Error && (error as ApiError).status)
      ) {
        throw error;
      }
      throw new Error(`API request failed: ${error}`);
    }
  }

  async requestSSE(route: string, signal?: AbortSignal): Promise<Response> {
    const headers = this.getHeaders();

    const response = await fetch(`${this.baseUrl}${route}`, {
      method: "GET",
      headers,
      credentials: "include",
      signal,
    });

    if (response.status === 401) {
      this.clearLocalState();
      throw redirect("/login");
    }

    if (response.status === 404) {
      throw redirect("/404");
    }

    if (!response.ok) {
      const error = new Error(
        `HTTP error! status: ${response.status}`
      ) as ApiError;
      try {
        error.responseData = await response.json();
      } catch {
        error.responseData = null;
      }
      error.status = response.status;
      throw error;
    }

    return new Response(response.body, {
      headers: {
        "Content-Type": "text/event-stream",
        "Cache-Control": "no-cache",
        Connection: "keep-alive",
      },
    });
  }

  get<T = any>(
    route: string,
    options?: Omit<ApiClientOptions, "method">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(route, { ...options, method: "GET" });
  }

  post<T = any>(
    route: string,
    body?: object | FormData,
    options?: Omit<ApiClientOptions, "method" | "body">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(route, { ...options, method: "POST", body });
  }

  put<T = any>(
    route: string,
    body?: object | FormData,
    options?: Omit<ApiClientOptions, "method" | "body">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(route, { ...options, method: "PUT", body });
  }

  delete<T = any>(
    route: string,
    body?: object | FormData,
    options?: Omit<ApiClientOptions, "method" | "body">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(route, { ...options, method: "DELETE", body });
  }

  patch<T = any>(
    route: string,
    body?: object | FormData,
    options?: Omit<ApiClientOptions, "method" | "body">
  ): Promise<ApiResponse<T>> {
    return this.request<T>(route, { ...options, method: "PATCH", body });
  }
}

export const apiClient = new ApiClient();
